'use client';

import { c as _c } from "react/compiler-runtime";
import { TableCell, TableRow } from '../components';
import { ChildRows } from './ChildRows';
import type { DataTableExpansion } from './types';
import type { ExpandedState, Row } from '@tanstack/react-table';

/**
 * Mirrors TanStack's own expanded check, but reads the state passed as a prop: the row
 * model is stable, so only that prop makes this body redraw when a row expands.
 *
 * A state of `true` means every row, which is what an expand-all control sets, so callers
 * pair this with `getCanExpand()` to keep a non-expandable row (no chevron) from sprouting
 * expanded content.
 */
export function isRowExpanded(expanded: ExpandedState | undefined, rowId: string): boolean {
  if (!expanded) return false;
  return expanded === true || !!expanded[rowId];
}

/** Number of leading feature columns (checkbox, chevron) a child row has to skip past. */
function countLeadingFeatureCells<TData>(row: Row<TData>): number {
  const cells = row.getVisibleCells();
  let count = 0;
  while (count < cells.length) {
    const meta = cells[count].column.columnDef.meta;
    if (!meta?.isSelectionColumn && !meta?.isExpandColumn) break;
    count += 1;
  }
  return count;
}
export interface ExpandedRowsProps<TData, TChildRow> {
  row: Row<TData>;
  expansion: DataTableExpansion<TData, TChildRow>;
  columnCount: number;
  inset: boolean;
}
export function ExpandedRows(t0) {
  const $ = _c(21);
  const {
    row,
    expansion,
    columnCount,
    inset
  } = t0;
  const {
    renderExpandedContent,
    getSubRows,
    childColumns,
    onChildRowClick,
    childRowHref
  } = expansion;
  if (renderExpandedContent) {
    let t1;
    if ($[0] !== renderExpandedContent || $[1] !== row.original) {
      t1 = renderExpandedContent(row.original);
      $[0] = renderExpandedContent;
      $[1] = row.original;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== columnCount || $[4] !== inset || $[5] !== t1) {
      t2 = <TableRow className="bg-background-secondary"><TableCell className="whitespace-normal wrap-anywhere" colSpan={columnCount} inset={inset}>{t1}</TableCell></TableRow>;
      $[3] = columnCount;
      $[4] = inset;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t1;
  if ($[7] !== getSubRows || $[8] !== row) {
    t1 = getSubRows?.(row.original);
    $[7] = getSubRows;
    $[8] = row;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const childRows = t1;
  if (!childRows?.length || !childColumns?.length) {
    return null;
  }
  let t2;
  if ($[10] !== row) {
    t2 = countLeadingFeatureCells(row);
    $[10] = row;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== childColumns || $[13] !== childRowHref || $[14] !== childRows || $[15] !== columnCount || $[16] !== inset || $[17] !== onChildRowClick || $[18] !== row.original || $[19] !== t2) {
    t3 = <ChildRows childColumns={childColumns} childRowHref={childRowHref} childRows={childRows} inset={inset} leadingCellCount={t2} onChildRowClick={onChildRowClick} parentColumnCount={columnCount} parentRow={row.original} />;
    $[12] = childColumns;
    $[13] = childRowHref;
    $[14] = childRows;
    $[15] = columnCount;
    $[16] = inset;
    $[17] = onChildRowClick;
    $[18] = row.original;
    $[19] = t2;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t3;
}